/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Grounded in docs/agent-context/subsystems/schedule-calendar.md
 * Marketing Constraints.
 *
 * Schedule and Calendar are SEPARATE marketing targets:
 *   - Schedule (THIS page) = dispatch board, My Day, project
 *     timelines, service visit scheduling. Active / operator UI.
 *   - Calendar (/features/calendar) = viewing events, iCal
 *     subscription, entity-driven auto-create. Reactive / observer.
 *
 * The combined Schedule page has 4 tabs in production:
 *   1. Dispatch (manager-only)
 *   2. My Day (field-worker view)
 *   3. Project Timeline (Gantt)
 *   4. Service Schedule (recurring visit calendar)
 *
 * Dispatch + My Day are real component files under
 * components/features/schedule/dispatch/. Project Timeline and
 * Service Schedule are rendered inline in the page.tsx via
 * lazy-loaded Gantt view components — do not imply they are
 * separate dedicated features.
 *
 * Route optimization exists via Mapbox — do NOT name the provider
 * in marketing copy.
 */
"use client";

import {
  FeaturePageTemplate,
  type FeaturePageConfig,
} from "@/components/landing/feature-page-template";
import {
  Calendar as CalendarIcon,
  Sunrise,
  GanttChartSquare,
  Repeat,
  AlertCircle,
  Users,
  Clock,
  Briefcase,
  Hammer,
  DollarSign,
  FileText,
  MousePointer2,
  Route,
} from "lucide-react";

const config: FeaturePageConfig = {
  badge: "Schedule & Dispatch",
  title: "Four views of your week.",
  titleHighlight: "One screen your whole team runs on.",
  description:
    "Coordinate dispatch, field assignments, long-range project timelines, and recurring service work from one schedule surface.",

  featureGrid: {
    title: "Everything scheduling has to do on a real job site",
    items: [
      {
        icon: CalendarIcon,
        title: "Dispatch Board",
        description:
          "Plan crew assignments across the week with conflict awareness before problems hit the field.",
      },
      {
        icon: Sunrise,
        title: "My Day (Field Worker View)",
        description:
          "Give field users a focused daily view with only their assignments and context.",
      },
      {
        icon: GanttChartSquare,
        title: "Project Timeline",
        description:
          "Use timeline views for longer-horizon planning across active project phases.",
      },
      {
        icon: Repeat,
        title: "Service Schedule",
        description:
          "Manage recurring service visits on a calendar so maintenance workflows stay predictable.",
      },
      {
        icon: MousePointer2,
        title: "Drag-Drop Assignments",
        description:
          "Move a task from one day to another, one crew to another, one worker to another — drag-drop. The underlying records update, notifications fire, and conflicts are flagged.",
      },
      {
        icon: AlertCircle,
        title: "Real-Time Conflict Detection",
        description:
          "Assigning the same foreman to two overlapping jobs? CC flags it as you drag. No more discovering conflicts on Monday morning when the crew shows up to the wrong site.",
      },
      {
        icon: Route,
        title: "Multi-Stop Route Optimization",
        description:
          "For crews hitting multiple job sites in a day, route optimization builds a sensible sequence — shortest drives, accounting for geography. Less windshield time, more billable hours.",
      },
      {
        icon: Users,
        title: "Crew Management",
        description:
          "Dedicated Crews page with crew members, assignments, and availability. Build crews once, assign them to work instead of picking individual people each time.",
      },
      {
        icon: Clock,
        title: "Calendar Sync for Assignments",
        description:
          "Assignments generated on the dispatch board flow into the shared calendar so every team member sees their day alongside existing commitments.",
      },
    ],
  },

  workflow: {
    title: "How a week actually runs through the Schedule page",
    description:
      "A practical weekly rhythm from planning through dispatch and completion tracking.",
    steps: [
      {
        label: "Plan",
        title: "Set the week on the Dispatch board",
        description:
          "Build the week by assigning work from backlog to crews with visibility into load, gaps, and overlaps.",
        details: [
          "Week view of every crew across every day",
          "Backlog pane shows unassigned work orders",
          "Drag-drop assignment with live conflict flags",
          "Gaps and over-scheduling visible at a glance",
          "Manager-only tab — field workers can't see the whole board",
        ],
      },
      {
        label: "Optimize",
        title: "Route-optimize multi-stop days",
        description:
          "Reduce windshield time by sequencing multi-stop workdays with route-aware planning support.",
        details: [
          "Multi-stop route optimization",
          "Respects geography and job constraints",
          "Route-order guidance for multi-stop days",
          "Review and manually override if needed",
          "Sequence flows to the field worker's My Day view",
        ],
      },
      {
        label: "Dispatch",
        title: "Field workers see their day and nothing else",
        description:
          "Keep field execution focused with assignment detail, routing context, and the key task documents needed on site.",
        details: [
          "Clean, phone-friendly daily task list",
          "Clock-in / out integrated",
          "Task details, location, and linked documents",
          "Route order matches the optimized sequence",
          "Respects role scoping — only assigned work is visible",
        ],
      },
      {
        label: "Track Long-Term",
        title: "Project Timeline for the critical-path view",
        description:
          "Use timeline views to understand milestone sequencing and longer-term planning risk.",
        details: [
          "Gantt view per project",
          "Milestone and phase timeline visibility",
          "Task sequence planning context",
          "Month / quarter / year horizons",
          "Built to stay smooth on larger projects",
        ],
      },
      {
        label: "Recur",
        title: "Service Schedule auto-populates recurring visits",
        description:
          "Define recurring service patterns once and manage generated visits with rescheduling flexibility.",
        details: [
          "Visit auto-generation from service frequency",
          "Calendar view of upcoming service visits",
          "Reschedule individual visits without breaking the pattern",
          "Completion tracking per visit",
          "Service billing rolls completed visits into period invoices",
        ],
      },
    ],
  },

  capabilities: [
    {
      icon: CalendarIcon,
      title: "Four Tabs, Four Jobs — Not Four Apps",
      description:
        "Run weekly scheduling through role-specific views that share the same underlying operational context.",
      bullets: [
        "Dispatch tab: manager week view with drag-drop",
        "My Day tab: field worker daily task list",
        "Project Timeline tab: Gantt view across projects",
        "Service Schedule tab: recurring visit calendar",
        "All four tabs stay in sync on the same live schedule data",
      ],
    },
    {
      icon: AlertCircle,
      title: "Conflict Detection That Catches Mistakes At Drag Time",
      description:
        "Detect scheduling conflicts early during dispatch planning so teams avoid costly field-day disruptions.",
      bullets: [
        "Real-time conflict detection during drag-drop",
        "Overlap flagging for crew members and crew leaders",
        "Over-scheduling warnings per crew per day",
        "Clear visual indicators on conflicts",
        "Prevention is cheaper than the 6am phone call",
      ],
    },
    {
      icon: Route,
      title: "Route Optimization For Multi-Stop Days",
      description:
        "Optimize stop order on multi-visit days to reduce travel waste and protect productive crew time.",
      bullets: [
        "Multi-stop daily route optimization",
        "Respects job time windows and constraints",
        "Clear route-order guidance for dispatch planning",
        "Override and manual reorder supported",
        "Optimized sequence flows to My Day view",
      ],
    },
    {
      icon: Repeat,
      title: "Recurring Services Schedule Themselves",
      description:
        "Automate recurring visit generation so service operations stay consistent without manual schedule duplication.",
      bullets: [
        "Visit auto-generation from service frequency",
        "Full-year visit population with one pattern definition",
        "Individual visit reschedule without breaking the pattern",
        "Completion tracking per visit",
        "Completed visits roll into service billing automatically",
      ],
    },
  ],

  connections: {
    title: "How the schedule connects to the rest of CC",
    items: [
      {
        icon: Briefcase,
        flow: "Project Tasks → Dispatch Board",
        description:
          "Project tasks (created from WON estimates) flow into the dispatch backlog automatically. Drag them onto the week to assign crews.",
      },
      {
        icon: Hammer,
        flow: "Service Pattern → Service Visits",
        description:
          "Recurring service patterns auto-generate visits for the whole year. The Service Schedule tab shows them on a calendar and lets you reschedule individuals without breaking the pattern.",
      },
      {
        icon: Clock,
        flow: "My Day → Clock In → Time Entries",
        description:
          "Field workers open My Day, clock in from the same view, and their time entries flow into project actual costs after manager approval.",
      },
      {
        icon: CalendarIcon,
        flow: "Dispatch Assignment → Calendar Sync",
        description:
          "Assignments generated on the dispatch board sync into the shared calendar so every team member sees their schedule alongside existing commitments.",
      },
      {
        icon: DollarSign,
        flow: "Service Visits → Service Billing",
        description:
          "Completed service visits roll into period-based service invoices automatically so scheduling stays tied to billing.",
      },
    ],
  },

  relatedFeatures: [
    {
      label: "Field Ops & Time Tracking",
      href: "/features/field-ops",
      icon: Sunrise,
    },
    {
      label: "Calendar",
      href: "/features/calendar",
      icon: CalendarIcon,
    },
    {
      label: "Project Management",
      href: "/features/project-management",
      icon: Briefcase,
    },
    {
      label: "Budgets & Invoices",
      href: "/features/budgets-invoices",
      icon: DollarSign,
    },
    {
      label: "Team & Permissions",
      href: "/features/collaboration",
      icon: Users,
    },
    {
      label: "Documents",
      href: "/features/documents",
      icon: FileText,
    },
  ],
};

export function SchedulePageClient() {
  return <FeaturePageTemplate config={config} />;
}
